/**
 * ConfirmProvider — the ONE shared CENTERED-MODAL service for BLOCKING messages
 * (BO UI Standard §13, two-tier message standard, ruling 2026-07-05).  Mounted
 * once at the app root.  Handles Tier-1 (blocking): Confirmation · Error ·
 * Warning · milestone Success.  Tier-2 (transient Success / Info) stays on the
 * top banner (useNotify); Tier-3 (single-field validation) stays inline.
 *
 * Two ways to raise a dialog:
 *   • Hook (inside a component):
 *       const { confirm, alert } = useConfirm();
 *       if (await confirm('Do you want to Save?')) { … }
 *   • GLOBAL functions (anywhere, even non-React code) — the standard's
 *     showDialog/warn/errorDlg/confirmDlg, backed by the module {@link dialogBus}:
 *       warn('Required Fields Missing', { list: ['Incubator ID'] });
 *       errorDlg('Could not reach the server.');
 *
 * This provider registers the real renderer into the bus on mount, and reroutes
 * of `useNotify().error` / `.warning` land here too.  Requests QUEUE — a new
 * dialog never clobbers one already open.
 */
import {
  createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';
import {
  registerDialogRunner, type DialogRequest, type DialogResult, type DialogType,
} from './dialogBus';
import './confirm.css';

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
}
export interface AlertOptions {
  title?: string;
  message: string;
  okLabel?: string;
  tone?: 'success' | 'error' | 'info' | 'warning';
}
/** Text-input prompt (e.g. the audit "reason for change" — CLAUDE.md §14b.2 /
 *  standing rule 11).  Resolves the entered text, or null when cancelled. */
export interface PromptOptions {
  title?: string;
  message: string;
  placeholder?: string;
  /** When true, OK stays disabled until non-whitespace text is entered. */
  required?: boolean;
  /** Pre-fill the textarea (e.g. editing an existing Review Log comment). */
  initialValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}
export interface ConfirmApi {
  confirm: (opts: ConfirmOptions | string) => Promise<boolean>;
  alert: (opts: AlertOptions | string) => Promise<void>;
  prompt: (opts: PromptOptions | string) => Promise<string | null>;
  /** The rich Tier-1 blocking dialog (also reachable globally via showDialog). */
  showDialog: (req: DialogRequest) => Promise<DialogResult>;
}

export const ConfirmContext = createContext<ConfirmApi | null>(null);

// Only the text-input PROMPT keeps its own state + input dialog; Confirmation,
// Alert and every showDialog blocking message now share the ONE RichDialog
// (§13 single modal family) via a 'dialog' state — confirm/alert are thin
// wrappers over it (2026-07-06).
interface PromptState {
  kind: 'prompt';
  opts: Required<Omit<PromptOptions, 'title'>> & { title: string };
  resolve: (v: string | null) => void;
}
interface ShowDialogState {
  kind: 'dialog';
  req: DialogRequest;
  resolve: (v: DialogResult) => void;
}
type DialogState = PromptState | ShowDialogState;

/** Per-type glyph + default title.  Colour lives ONLY in confirm.css
 *  (--ai-dialog-* tokens), never here — the dialog carries no hex. */
const DIALOG_GLYPH: Record<DialogType, { glyph: string; defaultTitle: string }> = {
  confirm: { glyph: '?', defaultTitle: 'Confirm' },
  error:   { glyph: '✕', defaultTitle: 'Error' },
  warning: { glyph: '⚠', defaultTitle: 'Warning' },
  success: { glyph: '✓', defaultTitle: 'Success' },
  info:    { glyph: 'i', defaultTitle: 'Notice' },
};

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  /** Live text for the prompt variant. */
  const [promptText, setPromptText] = useState('');
  /** Pending dialogs — a new request queues behind an open one (never clobbers). */
  const queue = useRef<DialogState[]>([]);

  // Show `state` now if nothing is open, else queue it.  Kept in a ref-safe
  // callback so the module bus can call it without a stale closure.
  const enqueue = useCallback((state: DialogState) => {
    setDialog((cur) => {
      if (cur) { queue.current.push(state); return cur; }
      return state;
    });
  }, []);

  // Confirm — a Yes/No blocking dialog rendered by the shared RichDialog (§13
  // blue Confirmation).  `danger` tone maps to the red Error styling.
  const confirm = useCallback((o: ConfirmOptions | string) => {
    const opts = typeof o === 'string' ? { message: o } : o;
    return new Promise<boolean>((resolve) => {
      enqueue({
        kind: 'dialog',
        req: {
          type: opts.tone === 'danger' ? 'error' : 'confirm',
          title: opts.title,
          message: opts.message,
          confirmLabel: opts.confirmLabel ?? 'Yes',
          cancelLabel: opts.cancelLabel ?? 'No',
        },
        resolve: (v) => resolve(v === true),
      });
    });
  }, [enqueue]);

  // Alert — a single-OK acknowledgement rendered by the shared RichDialog; the
  // tone (success / error / warning / info) drives the icon + coloured border.
  const alert = useCallback((o: AlertOptions | string) => {
    const opts = typeof o === 'string' ? { message: o } : o;
    return new Promise<void>((resolve) => {
      enqueue({
        kind: 'dialog',
        req: {
          type: (opts.tone ?? 'info') as DialogType,
          title: opts.title,
          message: opts.message,
          okLabel: opts.okLabel ?? 'OK',
        },
        resolve: () => resolve(),
      });
    });
  }, [enqueue]);

  const prompt = useCallback((o: PromptOptions | string) => {
    const opts = typeof o === 'string' ? { message: o } : o;
    return new Promise<string | null>((resolve) => {
      enqueue({
        kind: 'prompt',
        opts: {
          title: opts.title ?? 'Reason',
          message: opts.message,
          placeholder: opts.placeholder ?? '',
          required: opts.required ?? true,
          initialValue: opts.initialValue ?? '',
          confirmLabel: opts.confirmLabel ?? 'OK',
          cancelLabel: opts.cancelLabel ?? 'Cancel',
        },
        resolve,
      });
    });
  }, [enqueue]);

  const showDialog = useCallback((req: DialogRequest) => {
    return new Promise<DialogResult>((resolve) => {
      enqueue({ kind: 'dialog', req, resolve });
    });
  }, [enqueue]);

  // Register the real renderer into the module bus so the GLOBAL showDialog /
  // warn / errorDlg / confirmDlg (and the rerouted useNotify error/warning)
  // land here.  One provider is mounted app-wide.
  useEffect(() => {
    registerDialogRunner(showDialog);
    return () => registerDialogRunner(null);
  }, [showDialog]);

  // Pre-fill the prompt textarea with its initialValue whenever a prompt dialog
  // is surfaced (e.g. editing an existing Review Log comment).  `settle` clears
  // it to '' after resolving, so a plain prompt still opens empty.
  useEffect(() => {
    if (dialog?.kind === 'prompt' && dialog.opts.initialValue) setPromptText(dialog.opts.initialValue);
  }, [dialog]);

  const api = useMemo<ConfirmApi>(
    () => ({ confirm, alert, prompt, showDialog }),
    [confirm, alert, prompt, showDialog],
  );

  // Resolve the current dialog, then surface the next queued one (if any).
  const settle = useCallback((d: DialogState, result: boolean | DialogResult) => {
    if (d.kind === 'prompt') d.resolve(result ? promptText.trim() : null);
    else d.resolve(result as DialogResult);   // 'dialog'
    setPromptText('');
    const next = queue.current.shift() ?? null;
    setDialog(next);
  }, [promptText]);

  return (
    <ConfirmContext.Provider value={api}>
      {children}
      {dialog && dialog.kind === 'dialog' && (
        <RichDialog req={dialog.req} onResolve={(v) => settle(dialog, v)} />
      )}
      {/* PROMPT keeps the text-input dialog (blue header + textarea). */}
      {dialog && dialog.kind === 'prompt' && (
        <div className="cfd-overlay" role="presentation" onClick={() => settle(dialog, false)}>
          <div
            className="cfd-box cfd-default"
            role="alertdialog"
            aria-modal="true"
            aria-label={dialog.opts.title}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cfd-hdr">
              <i className="ti ti-help-circle" />
              <span>{dialog.opts.title}</span>
            </div>
            <div className="cfd-body">
              {dialog.opts.message}
              <textarea
                className="cfd-input"
                autoFocus
                rows={3}
                placeholder={dialog.opts.placeholder}
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
              />
            </div>
            <div className="cfd-foot">
              <button className="cfd-btn cfd-btn-ghost" onClick={() => settle(dialog, false)}>
                {dialog.opts.cancelLabel}
              </button>
              <button
                className="cfd-btn cfd-btn-primary"
                onClick={() => settle(dialog, true)}
                disabled={dialog.opts.required && promptText.trim().length === 0}
              >
                {dialog.opts.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

/**
 * RichDialog — the Tier-1 blocking dialog exactly as the BO UI Standard §13
 * shows it: centered card, icon + 2px coloured border by type, optional bullet
 * list (Warning) / detail rows + big ID (milestone Success), footer buttons
 * (custom actions, or the default OK / Yes-No), orange primary.
 */
function RichDialog({ req, onResolve }: { req: DialogRequest; onResolve: (v: DialogResult) => void }) {
  const s = DIALOG_GLYPH[req.type];
  const title = req.title ?? s.defaultTitle;
  // A two-button confirm when it's a confirm type OR carries a cancel label
  // (a `danger` confirm renders as red Error styling but keeps Cancel + Yes).
  const isConfirm = req.type === 'confirm' || req.cancelLabel != null;

  // Colour comes ONLY from the `cfd-t-<type>` class (confirm.css → --ai-dialog-*
  // tokens) — no hex here, so a dialog never uses its own colours.
  return (
    <div className="cfd-overlay" role="presentation" onClick={() => onResolve(isConfirm ? false : true)}>
      <div
        className={`cfd-rich cfd-t-${req.type}`}
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon — a coloured tile for confirm/error/success/info; a bare ⚠ for warning. */}
        {req.type === 'warning'
          ? <div className="cfd-rich-warnglyph" aria-hidden="true">⚠️</div>
          : <div className="cfd-rich-tile" aria-hidden="true">{s.glyph}</div>}

        <div className="cfd-rich-title">{title}</div>

        {req.message && <div className="cfd-rich-msg">{req.message}</div>}

        {/* Warning bullet list (e.g. missing required fields). */}
        {req.list && req.list.length > 0 && (
          <div className="cfd-rich-list">
            {req.list.map((item, i) => (
              <div key={i} className="cfd-rich-list-row">
                <span className="cfd-rich-x">✗</span>{item}
              </div>
            ))}
          </div>
        )}

        {/* Milestone success — big highlighted ID. */}
        {req.idValue && (
          <>
            {req.idLabel && <div className="cfd-rich-idlabel">{req.idLabel}</div>}
            <div className="cfd-rich-idvalue">{req.idValue}</div>
          </>
        )}

        {/* Detail rows (Status / Rows / Incubator …). */}
        {req.rows && req.rows.length > 0 && (
          <div className="cfd-rich-rows">
            {req.rows.map((r, i) => (
              <div key={i} className="cfd-rich-rows-row">
                <span className="cfd-rich-rows-k">{r.label}</span>
                <b>{r.value}</b>
              </div>
            ))}
          </div>
        )}

        {req.note && <div className="cfd-rich-note">{req.note}</div>}

        <div className="cfd-rich-foot">
          {req.actions && req.actions.length > 0 ? (
            req.actions.map((a, i) => (
              <button
                key={i}
                className={`cfd-btn ${a.primary ? 'cfd-btn-primary' : 'cfd-btn-ghost'}`}
                onClick={() => onResolve(a.value ?? a.label)}
                autoFocus={a.primary}
              >
                {a.label}
              </button>
            ))
          ) : isConfirm ? (
            <>
              <button className="cfd-btn cfd-btn-ghost" onClick={() => onResolve(false)}>{req.cancelLabel ?? 'Cancel'}</button>
              <button className="cfd-btn cfd-btn-primary" onClick={() => onResolve(true)} autoFocus>{req.confirmLabel ?? 'Yes'}</button>
            </>
          ) : (
            <button className="cfd-btn cfd-btn-primary" onClick={() => onResolve(true)} autoFocus>{req.okLabel ?? 'OK'}</button>
          )}
        </div>
      </div>
    </div>
  );
}

